// src/App.js
import { useState, useEffect, useRef, useCallback } from 'react';
import Dashboard from './dashboard';
import Customers from './customers';
import Inventory from './inventory';
import CannedJobs from './cannedjobs';
import Expenses from './expenses';
import Mileage from './mileage';
import Estimates from './estimates';
import Invoices from './invoices';
import Calendar from './calendar';
import Settings from './Settings';
import TaxCenterUI from './taxcenter_ui';
import IndustrialHeader from './IndustrialHeader';
import './mobile.css';
import { getTenantHeaders } from './api';

// ✅ Public pages
import PublicEstimatePage from './public/PublicEstimatePage';
import PublicInvoicePage from './public/PublicInvoicePage';
import PublicBookingPage from './public/PublicBookingPage';
import PublicAppointmentSchedulePage from './public/PublicAppointmentSchedulePage';

// ✅ Auth pages
import LoginPage from './LoginPage';
import ForgotPasswordPage from './ForgotPasswordPage';
import ResetPasswordPage from './ResetPasswordPage';
import SignupSuccessPage from './SignupSuccessPage';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5002';
const App = () => {
  const [currentView, setCurrentView] = useState('dashboard');
  const [pendingInvoiceId, setPendingInvoiceId] = useState(null);
  const [pendingInvoiceMode, setPendingInvoiceMode] = useState(null);
  const [pendingInvoiceDraft, setPendingInvoiceDraft] = useState(null);
  const [openNewEstimate, setOpenNewEstimate] = useState(false);
  const [openNewInvoice, setOpenNewInvoice] = useState(false);
  const authRequestRef = useRef(0);
  const appSoundRef = useRef(null);
  const entrySoundPlayedRef = useRef(false);
  const soundEnabledRef = useRef(false);
  const appWasHiddenRef = useRef(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try {
      return localStorage.getItem('mytoolnow_sound_enabled') === 'true';
    } catch (_error) {
      return false;
    }
  });

  const reportSoundDiagnostic = useCallback((message, error) => {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[MyTool NOW sound] ${message}`, error);
    }
  }, []);

  const playAppSound = useCallback((reason, preparedSound) => {
    if (!soundEnabledRef.current) return;
    try {
      const sound = preparedSound || appSoundRef.current || new Audio('/SOUND.mp3');
      appSoundRef.current = sound;
      sound.muted = false;
      sound.volume = 1;
      sound.currentTime = 0;
      void sound.play().catch((error) => {
        reportSoundDiagnostic(`Playback was blocked or failed during ${reason}.`, error);
      });
    } catch (error) {
      reportSoundDiagnostic(`Audio could not be initialized during ${reason}.`, error);
    }
  }, [reportSoundDiagnostic]);

  // --- Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [authUnavailable, setAuthUnavailable] = useState(false);

  // --- Detect URL for public pages and auth pages (full-screen)
  const [publicPage, setPublicPage] = useState(null);
  const [authPage, setAuthPage] = useState(null);
  const [billingError, setBillingError] = useState('');
  const [setupProgress, setSetupProgress] = useState(null);
  const [settingsModule, setSettingsModule] = useState(null);
  const [settingsReportId, setSettingsReportId] = useState(null);
  const [showStateTaxModal, setShowStateTaxModal] = useState(false);

  useEffect(() => {
    const path = window.location.pathname;
    // Public pages
    if (path.startsWith('/public/estimate/')) {
      setPublicPage({ type: 'estimate', token: path.split('/').pop() });
      setAuthChecked(true);
      return;
    } else if (path.startsWith('/public/invoice/')) {
      setPublicPage({ type: 'invoice', token: path.split('/').pop() });
      setAuthChecked(true);
      return;
    } else if (path.startsWith('/book/')) {
      setPublicPage({ type: 'booking' });
      setAuthChecked(true);
      return;
    } else if (path.startsWith('/s/')) {
      setPublicPage({ type: 'appointment-schedule', token: path.split('/').pop() });
      setAuthChecked(true);
      return;
    }
    // Auth pages
    if (path === '/forgot-password') {
      setAuthPage('forgot-password');
      setAuthChecked(true);
      return;
    }
    if (path.startsWith('/reset-password')) {
      setAuthPage('reset-password');
      setAuthChecked(true);
      return;
    }
    if (path.startsWith('/signup-success')) {
      setAuthPage('signup-success');
      setAuthChecked(true);
      return;
    }
    // All other paths — verify JWT
    verifyAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Verify existing JWT on app load
  const verifyAuth = async () => {
    const authRequestId = ++authRequestRef.current;
    const token = localStorage.getItem('auth_token');
    setAuthChecked(false);
    setAuthUnavailable(false);
    if (!token) {
      setIsAuthenticated(false);
      setAuthChecked(true);
      return;
    }

    const controller = new AbortController();
    const authTimeout = window.setTimeout(() => controller.abort(), 15000);

    try {
      const res = await fetch(`${API_BASE}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        signal: controller.signal
      });
      if (res.ok) {
        const data = await res.json();
        if (authRequestId !== authRequestRef.current) return;
        setIsAuthenticated(true);
        if (!entrySoundPlayedRef.current) {
          entrySoundPlayedRef.current = true;
          playAppSound('authenticated app entry');
        }
        // Keep tenant_id in sync
        localStorage.setItem('tenant_id', String(data.user.tenant_id));
        localStorage.setItem('tenantId', String(data.user.tenant_id));
        window.__TENANT_ID__ = data.user.tenant_id;
      } else if (res.status === 402) {
        if (authRequestId !== authRequestRef.current) return;
        // Subscription cancelled or past due — show billing wall
        const data = await res.json();
        clearAuth();
        setAuthPage('billing-wall');
        setBillingError(data.error || 'Your subscription is inactive.');
      } else if (res.status === 401) {
        if (authRequestId !== authRequestRef.current) return;
        // Token invalid or expired — clear it
        clearAuth();
      } else {
        if (authRequestId !== authRequestRef.current) return;
        console.error('Auth verification failed:', res.status);
        setAuthUnavailable(true);
      }
    } catch (error) {
      if (authRequestId !== authRequestRef.current) return;
      console.error('Auth verification request failed:', error);
      setAuthUnavailable(true);
    } finally {
      window.clearTimeout(authTimeout);
      if (authRequestId === authRequestRef.current) setAuthChecked(true);
    }
  };

  // --- Clear all auth data from localStorage
  const clearAuth = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_id');
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('tenantId');
    window.__TENANT_ID__ = null;
    entrySoundPlayedRef.current = false;
    setIsAuthenticated(false);
  };

  // --- Called by LoginPage on successful login
  const handleLoginSuccess = (user, preparedSound) => {
    setIsAuthenticated(true);
    if (!entrySoundPlayedRef.current) {
      entrySoundPlayedRef.current = true;
      playAppSound('successful login', preparedSound);
    }
    window.__TENANT_ID__ = user.tenant_id;
    setCurrentView('dashboard');
    if (user.needs_state_tax_setup) {
      const snoozed = localStorage.getItem('state_tax_setup_snoozed');
      if (!snoozed || Date.now() > Number(snoozed)) {
        setShowStateTaxModal(true);
      }
    }
  };

  // --- Logout
  const handleLogout = async () => {
    playAppSound('logout');
    try {
      await fetch(`${API_BASE}/api/auth/logout`, { method: 'POST' });
    } catch (e) {
      // Ignore network errors on logout
    }
    clearAuth();
  };

  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
    try {
      localStorage.setItem('mytoolnow_sound_enabled', soundEnabled ? 'true' : 'false');
    } catch (_error) {
      // The application remains usable if browser storage is unavailable.
    }
  }, [soundEnabled]);

  const handleSoundEnabledChange = useCallback(() => {
    const nextEnabled = !soundEnabledRef.current;
    soundEnabledRef.current = nextEnabled;
    setSoundEnabled(nextEnabled);
    if (nextEnabled) playAppSound('sound enabled confirmation');
  }, [playAppSound]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        appWasHiddenRef.current = true;
        return;
      }

      if (appWasHiddenRef.current && isAuthenticated) {
        appWasHiddenRef.current = false;
        playAppSound('return to app');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, playAppSound]);

  useEffect(() => {
    if (!isAuthenticated || !soundEnabled) return undefined;

    let lunchTimer;
    const scheduleLunchSound = () => {
      const now = new Date();
      const nextLunch = new Date(now);
      nextLunch.setHours(12, 0, 0, 0);
      if (nextLunch <= now) nextLunch.setDate(nextLunch.getDate() + 1);

      lunchTimer = window.setTimeout(() => {
        playAppSound('local noon reminder');
        scheduleLunchSound();
      }, nextLunch.getTime() - now.getTime());
    };

    scheduleLunchSound();
    return () => window.clearTimeout(lunchTimer);
  }, [isAuthenticated, soundEnabled, playAppSound]);


  const fetchSetupProgress = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/auth/setup-progress`, {
        headers: { 'Authorization': `Bearer ${token}`, ...getTenantHeaders() }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.dismissed) {
          setSetupProgress(null);
        } else {
          setSetupProgress(data);
        }
      }
    } catch (_e) {
      // non-fatal — bar simply won't render
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchSetupProgress();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, isAuthenticated]);

  useEffect(() => {
    if (currentView !== 'settings') {
      setSettingsModule(null);
      setSettingsReportId(null);
    }
  }, [currentView]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentView]);

  const navigate = (viewName, opts = {}) => {
    const shouldOpenNewEstimate = viewName === 'estimates' && opts.openNewEstimate === true;
    const shouldOpenNewInvoice = viewName === 'invoices' && opts.openNewInvoice === true;

    setOpenNewEstimate(shouldOpenNewEstimate);
    setOpenNewInvoice(shouldOpenNewInvoice);

    if (shouldOpenNewInvoice) {
      setPendingInvoiceId(null);
      setPendingInvoiceMode(null);
      setPendingInvoiceDraft(null);
    }

    if (opts.openInvoiceId) {
      setPendingInvoiceId(opts.openInvoiceId);
      setPendingInvoiceMode(opts.openInvoiceMode || 'entry');
    }
    if (opts.invoiceDraft) {
      setPendingInvoiceDraft(opts.invoiceDraft);
    }
    if (viewName === 'settings') {
      if (opts.settingsModule) setSettingsModule(opts.settingsModule);
      if (opts.reportId) setSettingsReportId(opts.reportId);
    }
    setCurrentView(viewName);
    window.scrollTo(0, 0);
  };

  // --- Styles
  const styles = {
    appContainer: {
      '--mytool-main-content-left': '0px',
      minHeight: '100vh',
      background: '#E8E2DA'
    },
    mainContent: {
      minHeight: '100vh'
    },
    loadingScreen: {
      minHeight: '100vh',
      backgroundColor: '#0a0a0a',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#FFD700',
      fontSize: '18px',
      fontFamily: "'Segoe UI', sans-serif",
      letterSpacing: '2px',
    }
  };

  // --- Public page full-screen rendering (no auth required)
  if (publicPage) {
    if (publicPage.type === 'estimate') {
      return <PublicEstimatePage token={publicPage.token} />;
    } else if (publicPage.type === 'invoice') {
      return <PublicInvoicePage token={publicPage.token} />;
    } else if (publicPage.type === 'booking') {
      return <PublicBookingPage />;
    } else if (publicPage.type === 'appointment-schedule') {
      return <PublicAppointmentSchedulePage token={publicPage.token} />;
    }
  }

  // --- Auth pages full-screen rendering (no auth required)
  if (authPage === 'forgot-password') {
    return <ForgotPasswordPage />;
  }
  if (authPage === 'reset-password') {
    return <ResetPasswordPage />;
  }
  if (authPage === 'signup-success') {
    return <SignupSuccessPage onLoginSuccess={handleLoginSuccess} />;
  }
  if (authPage === 'billing-wall') {
    const isPastDue = billingError.toLowerCase().includes('past due') || billingError.toLowerCase().includes('payment');
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0b1a3a 0%, #000 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ background: '#111827', border: '3px solid #E8761D', padding: 40, maxWidth: 480, width: '100%', boxSizing: 'border-box', textAlign: 'center' }}>
          <img src="/mtnlogo.png" alt="MyTool NOW" style={{ width: 180, height: 'auto', marginBottom: 24, filter: 'drop-shadow(0 0 14px rgba(232,118,29,0.6))' }} />
          <div style={{ fontWeight: 900, fontSize: 22, color: '#ffffff', fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>
            Account Access Issue
          </div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#d1d5db', fontFamily: 'Arial, sans-serif', lineHeight: 1.6, marginBottom: 24 }}>
            {isPastDue
              ? 'We were unable to process your recent payment. Please update your payment method to continue using MyTool NOW.'
              : 'Your MyTool NOW account is currently inactive. Please contact us and we will get you taken care of right away.'}
          </div>
          {isPastDue && (
            <a href="https://billing.stripe.com/p/login/live_bpc_1TMY7yPNI0rZLX4Cn1cwlFKg" style={{ display: 'block', width: '100%', padding: '14px', background: '#FFA500', color: '#000000', border: '4px solid #E8761D', fontWeight: 900, fontSize: 16, textTransform: 'uppercase', letterSpacing: 2, fontFamily: 'Arial, sans-serif', textDecoration: 'none', boxSizing: 'border-box', marginBottom: 16 }}>Update Payment Method</a>
          )}
          <a href="mailto:support@mytoolnow.com" style={{ display: 'block', color: '#FFA500', fontWeight: 700, fontFamily: 'Arial, sans-serif', fontSize: 14 }}>support@mytoolnow.com</a>
        </div>
      </div>
    );
  }

  // --- Wait for auth check to complete before rendering anything
  if (!authChecked) {
    return (
      <div style={styles.loadingScreen}>
        LOADING...
      </div>
    );
  }

  if (authUnavailable) {
    return (
      <div style={styles.loadingScreen}>
        <button type="button" onClick={verifyAuth}>
          SERVICE TEMPORARILY UNAVAILABLE — RETRY
        </button>
      </div>
    );
  }

  // --- Not authenticated — show login page
  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  // --- Authenticated — render full app
  return (
    <div style={styles.appContainer}>
      <div style={styles.mainContent}>
        <IndustrialHeader navigate={navigate} onLogout={handleLogout} />
        {currentView === 'dashboard' && <Dashboard navigate={navigate} soundEnabled={soundEnabled} onSoundEnabledChange={handleSoundEnabledChange} />}
        {currentView === 'customers' && <Customers navigate={navigate} />}
        {currentView === 'inventory' && <Inventory />}
        {currentView === 'cannedjobs' && <CannedJobs />}
        {currentView === 'expenses' && <Expenses />}
        {currentView === 'mileage' && <Mileage />}
        {currentView === 'estimates' && (
          <Estimates
            navigate={navigate}
            openNewEstimate={openNewEstimate}
            onNewEstimateOpened={() => setOpenNewEstimate(false)}
          />
        )}
        {currentView === 'invoices' && (
          <Invoices
            navigate={navigate}
            openNewInvoice={openNewInvoice}
            openInvoiceId={pendingInvoiceId}
            openInvoiceMode={pendingInvoiceMode}
            invoiceDraft={pendingInvoiceDraft}
            onNewInvoiceOpened={() => setOpenNewInvoice(false)}
            onInvoiceOpened={() => {
              setPendingInvoiceId(null);
              setPendingInvoiceMode(null);
              setPendingInvoiceDraft(null);
            }}
          />
        )}
        {currentView === 'calendar' && <Calendar />}
        {currentView === 'taxcenter' && <TaxCenterUI navigate={navigate} />}
        {currentView === 'settings' && <Settings defaultModule={settingsModule} reportId={settingsReportId} onReportOpened={() => setSettingsReportId(null)} navigate={navigate} setupProgress={setupProgress} />}
      </div>

      {showStateTaxModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.82)',
          zIndex: 9100,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px',
        }}>
          <div style={{
            background: '#0a1128',
            border: '4px solid #f59e0b',
            padding: '36px 40px',
            maxWidth: '480px', width: '100%',
            textAlign: 'center',
          }}>
            <div style={{
              fontWeight: 900, fontSize: '20px',
              color: '#f59e0b',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '16px',
            }}>
              ⚠️ TAX SETUP REQUIRED
            </div>
            <div style={{
              color: '#d1d5db', fontSize: '14px',
              fontWeight: 700, lineHeight: 1.65,
              marginBottom: '28px',
            }}>
              Your state tax information is not configured. Set up your state and county tax rates before creating invoices to ensure accurate tax calculations.
            </div>
            <button
              onClick={() => {
                setShowStateTaxModal(false);
                setSettingsModule('taxes');
                navigate('settings');
              }}
              style={{
                display: 'block', width: '100%',
                padding: '14px', marginBottom: '12px',
                background: '#f59e0b', color: '#000000',
                fontWeight: 900, fontSize: '15px',
                textTransform: 'uppercase', letterSpacing: '2px',
                border: 'none', cursor: 'pointer',
              }}
            >
              GO TO TAX SETTINGS NOW
            </button>
            <button
              onClick={() => {
                localStorage.setItem(
                  'state_tax_setup_snoozed',
                  String(Date.now() + 24 * 60 * 60 * 1000)
                );
                setShowStateTaxModal(false);
              }}
              style={{
                background: 'transparent',
                border: '2px solid #6b7280',
                color: '#9ca3af', fontWeight: 700,
                fontSize: '13px', padding: '10px 20px',
                cursor: 'pointer',
                textTransform: 'uppercase', letterSpacing: '1px',
              }}
            >
              REMIND ME IN 24 HOURS
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
